import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Check } from 'lucide-react';
import { scanProducts, pickWeightedProduct, type ScannedProduct } from '../lib/productScanner';
import { pickName, pickCity } from '../lib/purchaseData';
interface PurchaseToast {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
}
const FIRST_DELAY = 3000;
const VISIBLE_MS = 4000;
const MIN_GAP = 8000;
const MAX_GAP = 15000;
const SCAN_DELAY = 600;
function randomGap() {
  return Math.floor(MIN_GAP + Math.random() * (MAX_GAP - MIN_GAP));
}
export function RecentPurchaseToast() {
  const [products, setProducts] = useState<ScannedProduct[]>([]);
  const [toast, setToast] = useState<PurchaseToast | null>(null);
  const reduce = useReducedMotion();
  // Scan products once on mount (small delay so dynamic content has rendered)
  useEffect(() => {
    const t = setTimeout(() => {
      setProducts(scanProducts());
    }, SCAN_DELAY);
    return () => clearTimeout(t);
  }, []);
  // Single orchestrator: shows toasts on a loop
  useEffect(() => {
    if (products.length === 0) return;
    let cancelled = false;
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    const schedule = (cb: () => void, ms: number) => {
      const id = setTimeout(() => {
        if (!cancelled) cb();
      }, ms);
      timers.push(id);
    };
    const showOne = () => {
      if (cancelled) return;
      const newToast: PurchaseToast = {
        id: Date.now() + Math.random(),
        name: pickName(),
        city: pickCity(),
        product: pickWeightedProduct(products)
      };
      setToast(newToast);
      schedule(() => {
        setToast(null);
        schedule(showOne, randomGap());
      }, VISIBLE_MS);
    };
    schedule(showOne, FIRST_DELAY);
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [products]);
  const initial = reduce ? {
    opacity: 0,
    y: 0
  } : {
    opacity: 0,
    y: 24
  };
  const animate = {
    opacity: 1,
    y: 0
  };
  const exit = reduce ? {
    opacity: 0,
    y: 0
  } : {
    opacity: 0,
    y: 24
  };
  return <div aria-live="polite" aria-atomic="true" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[80] pointer-events-none w-full px-4 flex justify-center" data-id="element-323">
      <AnimatePresence data-id="element-324">
        {toast && <motion.div key={toast.id} initial={initial} animate={animate} exit={exit} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-white rounded-2xl shadow-xl border border-sage-100 p-3 pr-5 flex items-center gap-3 max-w-sm w-full" data-id="element-325">
            {/* Thumbnail */}
            <div className="flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden bg-cream-dark flex items-center justify-center" data-id="element-326">
              {toast.product.thumbnailUrl ? <img src={toast.product.thumbnailUrl} alt="" className="w-full h-full object-cover" data-id="element-327" /> : <div className="w-full h-full bg-sage-100" data-id="element-328" />}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0" data-id="element-329">
              <p className="text-xs text-charcoal-light leading-snug" data-id="element-330">
                <strong className="text-charcoal" data-id="element-331">{toast.name}</strong> en{' '}
                <span className="text-charcoal" data-id="element-332">{toast.city}</span>
              </p>
              <p className="text-xs text-charcoal mt-0.5 leading-snug truncate" data-id="element-333">
                acaba de adquirir <strong data-id="element-334">{toast.product.editionLabel}</strong>{' '}
                <span className="text-sage-700 font-semibold" data-id="element-335">
                  · {toast.product.priceText}
                </span>
              </p>
            </div>

            {/* Verified check */}
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-sage-100 flex items-center justify-center" data-id="element-336">
              <Check size={14} className="text-sage-700" strokeWidth={3} data-id="element-337" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}